import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
// Click-throughs are absolute http(s) URLs only (opened in a new tab). The
// bench hydrator ships nothing else, and this card renders on the admin
// origin, where a site-relative shopper path could only 404 — so relatives
// are dropped along with poisoned values (javascript:, data:, …), rendering
// as unlinked copy instead of a broken link.
import { anchorProps, safeAbsoluteHref } from '../Moments/href';
// One price format across every card on a moments page — this card renders in
// the admin shop pane beside the section tiles, and "$1,923" there must not
// read "$1923" here.
import { formatPrice } from '../Moments/formatPrice';
import './ProductCard.scss';

/*
  The full-bleed product card from Noir's request view (Figma "Edit Product
  card - new"), extracted as a presentational shell: photo fills the card, the
  brand / title·price / meta copy overlays the bottom on a scrim, and
  surface-specific controls render through the `actions` slot top-right. All
  app couplings (curation toggles, feedback, claim permissions) stay with the
  consumer. (The Figma card's top-left status chip is deliberately not ported
  — restore it from the source design when a consumer needs one.)
*/
const ProductCard = ({ image, brand, title, price, href, meta, actions, className }) => {
  // Catches dead-URL images (e.g. 404'd CDN links) that pass any server-side
  // presence filter — the card falls back to dark copy on the grey ground.
  const [imageFailed, setImageFailed] = useState(false);
  const hasImage = !!image && !imageFailed;
  const url = safeAbsoluteHref(href);

  const priceLabel = formatPrice(price);

  const body = (
    <>
      {hasImage && (
        <span className='shopmy-product-card-image'>
          <img src={image} alt={title || ''} loading='lazy' onError={() => setImageFailed(true)} />
        </span>
      )}

      <span className='shopmy-product-card-body'>
        <span className='shopmy-product-card-text'>
          {brand && <span className='shopmy-product-card-brand'>{brand}</span>}
          <span className='shopmy-product-card-titleprice'>
            {title}
            {priceLabel && <span className='shopmy-product-card-price'>{` · ${priceLabel}`}</span>}
          </span>
          {meta && <span className='shopmy-product-card-meta'>{meta}</span>}
        </span>
      </span>
    </>
  );

  return (
    <div className={cn('shopmy-shared-product-card', { 'has-image': hasImage }, className)}>
      {url ? (
        <a className='shopmy-product-card-link' {...anchorProps(url)}>
          {body}
        </a>
      ) : (
        <span className='shopmy-product-card-link'>{body}</span>
      )}
      {actions && <div className='shopmy-product-card-actions'>{actions}</div>}
    </div>
  );
};

ProductCard.propTypes = {
  image: PropTypes.string,
  brand: PropTypes.string,
  title: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  href: PropTypes.string,
  meta: PropTypes.node,
  actions: PropTypes.node,
  className: PropTypes.string
};

export default ProductCard;
